import React, { useCallback, useContext, useMemo, useRef } from 'react'
import { useIntl } from 'react-intl'
import FlightSearchWidgetStateContext from 'contexts/Flights/FlightSearchWidget/flightSearchWidgetStateContext'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import Group from 'components/utils/Group'
import config from 'constants/config'
import FlightSearchAirportModal from './FlightAirportSelect/FlightSearchAirportModal'
import ModalContext from 'contexts/ModalContext'
import SearchFormField from 'components/SearchV2/Components/SearchFormField/SearchFormField'
import FlightSearchMenuMobile from './FlightSearchMenu/FlightSearchMenuMobile'
import { addDays } from '@luxuryescapes/lib-refunds/build/lib/date'
import { DMY_CASUAL_FORMAT } from 'constants/dateFormats'
import SearchFormFieldGroup from 'components/SearchV2/Components/SearchFormField/SearchFormFieldGroup'
import FlightSearchTravellerSelectContent from './FlightTravellerSelect/FlightSearchTravellerSelectContent'
import { getAirportDisplayText } from 'lib/flights/flightUtils'
import useFlightDeal from 'hooks/Flights/useFlightDeal'
import useFlightDeals from 'hooks/Flights/useFlightDeals'
import FlightDealDuration from 'components/Flights/FlightMerchandising/FlightDealDuration/FlightDealDuration'
import FlightDealAgentHubDisclaimer from 'components/Flights/FlightMerchandising/FlightDealAgentHubDisclaimer/FlightDealAgentHubDisclaimer'
import { isAgentHubEnabled } from 'agentHub/selectors/agentHubSelectors'
import BodyText from 'LuxKit/Typography/BodyText'
import { FlightsFareTypes } from 'constants/flight'
import styled from 'styled-components'
import TextButton from 'LuxKit/Button/TextButton'
import LinePlusIcon from 'LuxKit/Icons/line/LinePlusIcon'
import Heading from 'LuxKit/Typography/Heading'
import {
  FlightSearchWidgetActions,
  createEmptyFlightSearchWidgetFlightItem,
} from 'contexts/Flights/FlightSearchWidget/flightSearchWidgetStateReducer'
import type { FlightSearchWidgetFlightItem } from 'contexts/Flights/FlightSearchWidget/types'
import FlightSearchWidgetDispatchContext from 'contexts/Flights/FlightSearchWidget/flightSearchWidgetDispatchContext'
import LineArrowsSwapVerticalIcon from 'LuxKit/Icons/line/LineArrowsSwapVerticalIcon'
import IconButton from 'LuxKit/Button/IconButton'
import { rem } from 'polished'
import FlightSearchMaxFlightsModal from './FlightSearchMaxFlightsModal'
import FlightsVerticalDatePicker from './FlightsDatePicker/FlightsVerticalDatePicker'

const ButtonsContainer = styled(Group)`
  & > button:not(:last-child) {
    border-bottom: 0;
  }
`

const AirportsContainer = styled(Group)`
  position: relative;

  & > button:not(.switch){
    border-bottom: 0;
  }
`

const ExchangeButtonContainer = styled.div`
  position: absolute;
  right: ${rem(16)};
  top: 50%;
  transform: translateY(-50%);
  border: 1px solid ${props => props.theme.palette.neutral.default.five};
  background-color: ${props => props.theme.palette.neutral.default.eight};
  border-radius: 50%;
`

interface Props {
  /**
   * Sets the airports to a 'read only' mode
   * Used when defaulting airport selections such as on flight deal pages
   */
  readOnlyAirports?: boolean
}

function FlightSearchWidgetMobileMultiCityForm(props: Props) {
  const { readOnlyAirports } = props
  const intl = useIntl()
  const state = useContext(FlightSearchWidgetStateContext)
  const dispatch = useContext(FlightSearchWidgetDispatchContext)
  const departingRefs = useRef<Array<HTMLButtonElement | null>>([])

  const { fareType, flightDealId, flights } = state
  const [flightDeal] = useFlightDeal(flightDealId)
  const [flightDeals] = useFlightDeals()
  const showModal = useContext(ModalContext)

  const travellersTitle = useMemo(
    () =>
      intl.formatMessage({
        id: 'M+8hnlubAR',
        defaultMessage: 'Travellers',
        description: 'Title for travellers selection modal in flight search widget',
      }),
    [intl],
  )

  const onTravellerSelect = useCallback(() => {
    showModal(
      <FlightSearchMenuMobile title={travellersTitle} showTravellers>
        <FlightSearchTravellerSelectContent />
      </FlightSearchMenuMobile>,
    )
  }, [showModal, travellersTitle])

  const earliestBookingDate = useMemo(() => addDays(new Date(), 1), [])
  const finalBookingDate = useMemo(() => addDays(new Date(), 330), [])
  const flightDealsMemoized = useMemo(() => (flightDeals || []), [flightDeals])

  const featuredRanges = useMemo(() => {
    return flightDeal?.travelPeriods.map(period => ({
      start: new Date(period.departureDate),
      end: new Date(period.arrivalDate),
    }))
  }, [flightDeal])

  const dateSelectTitle = useMemo(
    () =>
      intl.formatMessage({
        id: 'C36/0JIKVC',
        defaultMessage: 'When do you want to go?',
        description: 'Title for date selection modal in flight search widget',
      }),
    [intl],
  )

  const onDateSelect = useCallback(
    async(flightId: string) => {
      const dates = await showModal<any>(
        <FlightSearchMenuMobile
          title={dateSelectTitle}
          showDates
        >
          <Group
            direction="vertical"
            verticalAlign="space-between"
            gap={12}
            fullHeight
          >
            <FlightsVerticalDatePicker
              minDate={earliestBookingDate}
              maxDate={finalBookingDate}
              id={flightId}
              required
              featuredRanges={featuredRanges}
              flightDeals={flightDealsMemoized}
            />
            <VerticalSpacer gap={12}>
              {flightDeal && <FlightDealDuration flightDeal={flightDeal} />}
              {isAgentHubEnabled && <FlightDealAgentHubDisclaimer />}
              <BodyText variant="small" colour="neutral-two">
                {intl.formatMessage({
                  id: 'Hnli7vLI09',
                  defaultMessage: 'Based on recent return prices (per adult) found',
                  description: 'Message about recent return prices in flight date picker',
                })}
              </BodyText>
            </VerticalSpacer>
          </Group>
        </FlightSearchMenuMobile>,
      )

      const rowIndex = flights.findIndex(f => f.id === flightId)
      const pendingRows = rowIndex < flights.length - 1

      if (pendingRows) {
        departingRefs.current[rowIndex + 1]?.click()
      }

      if (dates && !pendingRows) {
        onTravellerSelect()
      }
    },
    [showModal, earliestBookingDate, finalBookingDate, featuredRanges, flightDeal, flights, onTravellerSelect, flightDealsMemoized, dateSelectTitle, intl],
  )

  const onArrivalSelect = useCallback(
    async(flightId: string) => {
      const airport = await showModal<App.AirportLocation | undefined>(
        <FlightSearchAirportModal direction="arrival" id={flightId} />,
      )

      if (airport) {
        onDateSelect(flightId)
      }
    },
    [onDateSelect, showModal],
  )

  const handleRemoveFlight = useCallback(
    (flightId: string) => {
      dispatch({
        type: FlightSearchWidgetActions.REMOVE_FLIGHT,
        payload: flightId,
      })
    },
    [dispatch],
  )

  const handleAddFlight = useCallback(() => {
    if (flights.length === config.FLIGHTS_MULTI_CITY_MAX_FLIGHTS) {
      showModal<boolean>(<FlightSearchMaxFlightsModal />)
      return
    }

    const item: FlightSearchWidgetFlightItem = createEmptyFlightSearchWidgetFlightItem()

    dispatch({
      type: FlightSearchWidgetActions.ADD_FLIGHT,
      payload: item,
    })
  }, [dispatch, flights.length, showModal])

  const onDepartureSelect = useCallback(
    async(flightId: string) => {
      if (readOnlyAirports) return

      const airport = await showModal<App.AirportLocation | undefined>(
        <FlightSearchAirportModal direction="departure" id={flightId} />,
      )

      if (airport) {
        onArrivalSelect(flightId)
      }
    },
    [onArrivalSelect, readOnlyAirports, showModal],
  )

  const onSwapAirports = useCallback((flightId: string) => {
    const currentFlight = flights.find(flight => flight.id === flightId)!
    const departure = currentFlight.departureAirport
    dispatch({ type: FlightSearchWidgetActions.SET_DEPARTURE_AIRPORT, id: flightId, airport: currentFlight.arrivalAirport })
    dispatch({ type: FlightSearchWidgetActions.SET_ARRIVAL_AIRPORT, id: flightId, airport: departure })
  }, [flights, dispatch])

  const showRemoveButton = flights.length > 1

  return (
    <VerticalSpacer gap={16}>
      {flights.map((flight, index) => (
        <VerticalSpacer key={flight.id} gap={8}>
          <Group
            direction="horizontal"
            gap="0 4"
            horizontalAlign="space-between"
            verticalAlign="center"
          >
            <Heading variant="heading6">
              {intl.formatMessage(
                {
                  id: 'd6Y5k/umby',
                  defaultMessage: 'Flight {number}',
                  description: 'Flight number label in multi-city flight search in the flights section',
                },
                { number: index + 1 },
              )}
            </Heading>

            {showRemoveButton && (
              <TextButton
                kind="tertiary"
                onClick={() => handleRemoveFlight(flight.id)}
              >
                {intl.formatMessage({
                  id: 'CSVuWo5/t6',
                  defaultMessage: 'Remove',
                  description: 'Button text to remove a flight in multi-city flight search',
                })}
              </TextButton>
            )}
          </Group>

          <ButtonsContainer direction="vertical">
            <AirportsContainer direction="vertical">
              <SearchFormField
                value={getAirportDisplayText(flight.departureAirport)}
                label={intl.formatMessage({
                  id: 'qD7oW0qPSP',
                  defaultMessage: 'From',
                  description: 'Label for the departure airport input field in multi-city flight search form',
                })}
                required
                placeholder={intl.formatMessage({
                  id: 'HRi6+dtcAk',
                  defaultMessage: 'Search by city or airport',
                  description: 'Placeholder text for airport search field in the search interface',
                })}
                onClick={() => onDepartureSelect(flight.id)}
                requiredErrorMessage={intl.formatMessage({
                  id: 'XT7iAbC3Uf',
                  defaultMessage: 'Please enter a departure',
                  description: 'Error message when departure airport is not selected',
                })}
                hasError={flight.showDepartureError}
                ref={(node) => {
                  departingRefs.current[index] = node
                }}
              />

              <SearchFormField
                value={getAirportDisplayText(flight.arrivalAirport)}
                label={intl.formatMessage({
                  id: '2UOCctw7EH',
                  defaultMessage: 'To',
                  description: 'Form field label for arrival airport field in flight search',
                })}
                required
                placeholder={intl.formatMessage({
                  id: 'HRi6+dtcAk',
                  defaultMessage: 'Search by city or airport',
                  description: 'Placeholder text for airport search field in the search interface',
                })}
                onClick={() => onArrivalSelect(flight.id)}
                requiredErrorMessage={intl.formatMessage({
                  id: '0gkTK6pcWo',
                  defaultMessage: 'Please enter a destination',
                  description: 'Error message when destination airport is not selected',
                })}
                hasError={flight.showDestinationError}
              />

              <ExchangeButtonContainer>
                <IconButton variant="default" kind="tertiary" size="medium" shape="circle" className="switch" onClick={() => onSwapAirports(flight.id)} disabled={readOnlyAirports}>
                  <LineArrowsSwapVerticalIcon size="M" />
                </IconButton>
              </ExchangeButtonContainer>
            </AirportsContainer>

            <SearchFormFieldGroup>
              <SearchFormField
                label={intl.formatMessage({
                  id: 'h715GKJI/T',
                  defaultMessage: 'Departing',
                  description: 'Form field label for departing date field in flight search',
                })}
                placeholder={intl.formatMessage({
                  id: 'Xnhjjfb2SQ',
                  defaultMessage: 'Select date',
                  description: 'Placeholder text for date selection field when field is empty',
                })}
                required
                requiredErrorMessage={intl.formatMessage({
                  id: 'QjCDP+dTWZ',
                  defaultMessage: 'Please select travel dates',
                  description: 'Error notification displayed when travel dates are not selected',
                })}
                value={flight.checkinDate?.format(DMY_CASUAL_FORMAT)}
                onClick={() => onDateSelect(flight.id)}
                hasError={flight.showTravelDatesError}
              />

              {fareType.value === FlightsFareTypes.RETURN && (
                <SearchFormField
                  label={intl.formatMessage({
                    id: 'wM+9lNMdAZ',
                    defaultMessage: 'Returning',
                    description: 'Form field label for returning date field in flight search',
                  })}
                  placeholder={intl.formatMessage({
                    id: 'Xnhjjfb2SQ',
                    defaultMessage: 'Select date',
                    description: 'Placeholder text for date selection field when field is empty',
                  })}
                  required
                  requiredErrorMessage={intl.formatMessage({
                    id: 'QjCDP+dTWZ',
                    defaultMessage: 'Please select travel dates',
                    description: 'Error notification displayed when travel dates are not selected',
                  })}
                  value={flight.checkoutDate?.format(DMY_CASUAL_FORMAT)}
                  onClick={() => onDateSelect(flight.id)}
                  hasError={flight.showTravelDatesError}
                />
              )}
            </SearchFormFieldGroup>
          </ButtonsContainer>
        </VerticalSpacer>
      ))}

      <TextButton
        kind="secondary"
        variant="dark"
        startIcon={<LinePlusIcon />}
        size="medium"
        onClick={handleAddFlight}
      >
        {intl.formatMessage({
          id: 'zZNaxnWtkP',
          defaultMessage: 'Add flight',
          description: 'Button text to add another flight in multi-city flight search',
        })}
      </TextButton>
    </VerticalSpacer>
  )
}

export default FlightSearchWidgetMobileMultiCityForm
